/**
 * useDataGridKeyboard
 *
 * Handles all keyboard events for cell navigation in the data grid.
 * Tab, Shift+Tab, Enter, Escape, Arrow keys, Home/End.
 * Plan 124, Epic 03: Shift+Arrow range extension, Tab/Enter within range.
 *
 * Global shortcuts (Ctrl+Z/C/V/X) are checked BEFORE the isCellEditing
 * guard so they intercept even when an input is focused.
 *
 * When `isCellEditing` is true, only Tab and Escape (plus globals above)
 * are handled — all other keys pass through to the input element.
 */

import { useCallback } from 'react';
import type { CellPosition, DataGridKeyboardOptions } from './types';
import {
  isMultiCellRange,
  getNextVisibleCol,
  getPrevVisibleCol,
  getNextEditableCellInRange,
  getNextCellInColumnInRange,
} from './dataGridRangeKeyboardHelpers';

export interface UseDataGridKeyboardReturn {
  handleKeyDown: (e: React.KeyboardEvent) => void;
}

export function useDataGridKeyboard(
  opts: DataGridKeyboardOptions,
): UseDataGridKeyboardReturn {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!opts.isEditMode) return;

      const { selectionRange, visibleColumnIds, rowCount } = opts;

      // ── Global shortcuts (Ctrl/Cmd + key) ──
      // Must be checked BEFORE isCellEditing guard so Ctrl+Z intercepts
      // instead of letting the browser undo text input.
      const key = e.key.toLowerCase(); // CRITICAL: Shift+Z gives uppercase 'Z'
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey) {
        switch (key) {
          case 'z': e.preventDefault(); opts.onUndo?.(); return;
          case 'c': e.preventDefault(); opts.onCopy?.(); return;
          case 'v': e.preventDefault(); opts.onPaste?.(); return;
          case 'x': e.preventDefault(); opts.onCut?.(); return;
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.shiftKey && key === 'z') {
        e.preventDefault(); opts.onRedo?.(); return;
      }
      if ((e.ctrlKey || e.metaKey) && key === 'y') {
        e.preventDefault(); opts.onRedo?.(); return;
      }

      // If no cell is active, only handle initial activation
      if (!opts.activeCell) {
        if (e.key === 'Tab' || e.key === 'ArrowDown') {
          const first = opts.getFirstInRow(0);
          if (first) {
            e.preventDefault();
            opts.selectCell(first);
          }
        }
        return;
      }

      const { activeCell } = opts;
      const hasRange = isMultiCellRange(selectionRange);

      // ── Helper: scroll focus cell into view after range extension ──
      const scrollFocusIntoView = (pos: CellPosition) => {
        const container = opts.scrollContainerRef?.current;
        if (!container) return;
        const td = container.querySelector(
          `[data-row-index="${pos.rowIndex}"][data-column-id="${pos.columnId}"]`,
        );
        td?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      };

      // ── Helper: extend range (Shift+key) ──
      const extendRange = (newFocus: CellPosition) => {
        const anchor = selectionRange?.anchor ?? activeCell;
        opts.setRange(anchor, newFocus);
        scrollFocusIntoView(newFocus);
      };

      // ── Helper: compute range bounds for Tab/Enter cycling ──
      const getRangeBounds = () => {
        if (!selectionRange) return null;
        const anchorColIdx = visibleColumnIds.indexOf(selectionRange.anchor.columnId);
        const focusColIdx = visibleColumnIds.indexOf(selectionRange.focus.columnId);
        if (anchorColIdx < 0 || focusColIdx < 0) return null;
        return {
          minRow: Math.min(selectionRange.anchor.rowIndex, selectionRange.focus.rowIndex),
          maxRow: Math.max(selectionRange.anchor.rowIndex, selectionRange.focus.rowIndex),
          minColIdx: Math.min(anchorColIdx, focusColIdx),
          maxColIdx: Math.max(anchorColIdx, focusColIdx),
        };
      };

      // ── Helper: Tab within range (used for both editing and non-editing) ──
      const tabWithinRange = (reverse: boolean): CellPosition | null => {
        const bounds = getRangeBounds();
        if (!bounds) return null;
        return getNextEditableCellInRange(
          activeCell, opts.editableColumnIds,
          bounds.minRow, bounds.maxRow, bounds.minColIdx, bounds.maxColIdx,
          visibleColumnIds, reverse,
        );
      };

      // When a cell input is focused, only intercept Tab and Escape.
      // All other keys (arrows, letters, Enter) pass through to the input.
      if (opts.isCellEditing) {
        switch (e.key) {
          case 'Tab': {
            e.preventDefault();
            opts.onCommitAndMove?.();
            // If range exists, cycle within range bounds
            if (hasRange) {
              const next = tabWithinRange(e.shiftKey);
              if (next) opts.selectCell(next);
            } else {
              const next = e.shiftKey
                ? opts.getPrevEditableCell(activeCell)
                : opts.getNextEditableCell(activeCell);
              if (next) opts.selectCell(next);
            }
            break;
          }
          case 'Escape': {
            e.preventDefault();
            e.nativeEvent.preventDefault();
            opts.onCancelEdit?.();
            opts.clearSelection();
            break;
          }
          default:
            break;
        }
        return;
      }

      // ── Cell is active but not editing (selection/navigation mode) ──

      // Determine focus cell for range operations
      const focus = selectionRange?.focus ?? activeCell;

      switch (e.key) {
        case 'Tab': {
          e.preventDefault();
          if (hasRange) {
            const next = tabWithinRange(e.shiftKey);
            if (next) opts.selectCell(next);
          } else {
            const next = e.shiftKey
              ? opts.getPrevEditableCell(activeCell)
              : opts.getNextEditableCell(activeCell);
            if (next) opts.selectCell(next);
          }
          break;
        }

        case 'Enter': {
          e.preventDefault();
          if (hasRange) {
            const bounds = getRangeBounds();
            if (bounds) {
              const next = getNextCellInColumnInRange(
                activeCell, opts.editableColumnIds,
                bounds.minRow, bounds.maxRow, bounds.minColIdx, bounds.maxColIdx,
                visibleColumnIds,
              );
              if (next) opts.selectCell(next);
            }
          } else {
            const below = opts.getCellBelow(activeCell);
            if (below) opts.selectCell(below);
          }
          break;
        }

        case 'Escape': {
          e.preventDefault();
          e.nativeEvent.preventDefault();
          opts.clearSelection();
          break;
        }

        case 'ArrowUp': {
          e.preventDefault();
          if (e.shiftKey) {
            const newRow = e.ctrlKey || e.metaKey ? 0 : Math.max(0, focus.rowIndex - 1);
            extendRange({ rowIndex: newRow, columnId: focus.columnId });
          } else if (hasRange) {
            opts.clearRange();
            const target = opts.getCellAbove(focus);
            opts.selectCell(target ?? focus);
          } else {
            const above = opts.getCellAbove(activeCell);
            if (above) opts.selectCell(above);
          }
          break;
        }

        case 'ArrowDown': {
          e.preventDefault();
          if (e.shiftKey) {
            const newRow = e.ctrlKey || e.metaKey
              ? rowCount - 1
              : Math.min(rowCount - 1, focus.rowIndex + 1);
            extendRange({ rowIndex: newRow, columnId: focus.columnId });
          } else if (hasRange) {
            opts.clearRange();
            const target = opts.getCellBelow(focus);
            opts.selectCell(target ?? focus);
          } else {
            const below = opts.getCellBelow(activeCell);
            if (below) opts.selectCell(below);
          }
          break;
        }

        case 'ArrowLeft': {
          e.preventDefault();
          if (e.shiftKey) {
            const newCol = e.ctrlKey || e.metaKey
              ? visibleColumnIds[0]
              : getPrevVisibleCol(focus.columnId, visibleColumnIds);
            if (newCol) extendRange({ rowIndex: focus.rowIndex, columnId: newCol });
          } else if (hasRange) {
            opts.clearRange();
            const prev = opts.getPrevEditableCell(focus);
            opts.selectCell(prev && prev.rowIndex === focus.rowIndex ? prev : focus);
          } else {
            const prev = opts.getPrevEditableCell(activeCell);
            if (prev && prev.rowIndex === activeCell.rowIndex) opts.selectCell(prev);
          }
          break;
        }

        case 'ArrowRight': {
          e.preventDefault();
          if (e.shiftKey) {
            const newCol = e.ctrlKey || e.metaKey
              ? visibleColumnIds[visibleColumnIds.length - 1]
              : getNextVisibleCol(focus.columnId, visibleColumnIds);
            if (newCol) extendRange({ rowIndex: focus.rowIndex, columnId: newCol });
          } else if (hasRange) {
            opts.clearRange();
            const next = opts.getNextEditableCell(focus);
            opts.selectCell(next && next.rowIndex === focus.rowIndex ? next : focus);
          } else {
            const next = opts.getNextEditableCell(activeCell);
            if (next && next.rowIndex === activeCell.rowIndex) opts.selectCell(next);
          }
          break;
        }

        case 'Home': {
          e.preventDefault();
          if (e.shiftKey) {
            const target = e.ctrlKey || e.metaKey
              ? { rowIndex: 0, columnId: visibleColumnIds[0] }
              : { rowIndex: focus.rowIndex, columnId: visibleColumnIds[0] };
            extendRange(target);
          } else {
            const target = (e.ctrlKey || e.metaKey)
              ? opts.getFirstCellInTable()
              : opts.getFirstInRow(activeCell.rowIndex);
            if (target) opts.selectCell(target);
          }
          break;
        }

        case 'End': {
          e.preventDefault();
          if (e.shiftKey) {
            const lastCol = visibleColumnIds[visibleColumnIds.length - 1];
            const target = e.ctrlKey || e.metaKey
              ? { rowIndex: rowCount - 1, columnId: lastCol }
              : { rowIndex: focus.rowIndex, columnId: lastCol };
            extendRange(target);
          } else {
            const target = (e.ctrlKey || e.metaKey)
              ? opts.getLastCellInTable()
              : opts.getLastInRow(activeCell.rowIndex);
            if (target) opts.selectCell(target);
          }
          break;
        }

        case 'PageDown': {
          e.preventDefault();
          if (e.shiftKey) {
            const newRow = Math.min(rowCount - 1, focus.rowIndex + 10);
            extendRange({ rowIndex: newRow, columnId: focus.columnId });
          } else {
            const target = opts.getCellPageDown(activeCell, 10);
            if (target) opts.selectCell(target);
          }
          break;
        }

        case 'PageUp': {
          e.preventDefault();
          if (e.shiftKey) {
            const newRow = Math.max(0, focus.rowIndex - 10);
            extendRange({ rowIndex: newRow, columnId: focus.columnId });
          } else {
            const target = opts.getCellPageUp(activeCell, 10);
            if (target) opts.selectCell(target);
          }
          break;
        }

        case 'Delete':
        case 'Backspace': {
          if (isMultiCellRange(selectionRange)) {
            e.preventDefault();
            opts.onDelete?.();
          }
          break;
        }

        case '?': {
          e.preventDefault();
          opts.onShowHelp?.();
          break;
        }

        default:
          break;
      }
    },
    [opts],
  );

  return { handleKeyDown };
}
